import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/discover?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky-nav" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
          <div className="w-8 h-8 bg-[#702A32] flex items-center justify-center">
            <span className="font-heading text-white text-xl leading-none">M</span>
          </div>
          <span className="font-heading text-xl tracking-tight text-[#1A1A18]">MapleMarket</span>
          <span className="overline hidden md:inline ml-1">Canada</span>
        </Link>

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md mx-8" data-testid="header-search-form">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8782]" strokeWidth={1.5} />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, stores, makers…"
              className="input-field pl-10"
            />
          </div>
        </form>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/discover" data-testid="nav-discover" className="btn-ghost">Discover</NavLink>
          <NavLink to="/stores" data-testid="nav-stores" className="btn-ghost">Stores</NavLink>
          <Link to="/cart" data-testid="nav-cart" className="btn-ghost relative inline-flex">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#702A32] text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count-badge">{count}</span>
            )}
          </Link>
          {user ? (
            <div className="relative group">
              <button className="btn-ghost inline-flex items-center gap-2" data-testid="user-menu-button">
                <User className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{user.name?.split(" ")[0]}</span>
              </button>
              <div className="absolute right-0 top-full mt-2 w-56 surface shadow-lg py-2 hidden group-hover:block z-30">
                <Link to="/account" className="block px-4 py-2 hover:bg-[#EFECE6] text-sm" data-testid="menu-account">
                  <LayoutDashboard className="w-4 h-4 inline mr-2" strokeWidth={1.5} />Account
                </Link>
                {user.role === "merchant" && (
                  <Link to="/merchant" className="block px-4 py-2 hover:bg-[#EFECE6] text-sm" data-testid="menu-merchant">
                    <Store className="w-4 h-4 inline mr-2" strokeWidth={1.5} />Merchant Dashboard
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link to="/admin" className="block px-4 py-2 hover:bg-[#EFECE6] text-sm" data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 inline mr-2" strokeWidth={1.5} />Admin Panel
                  </Link>
                )}
                <button onClick={logout} className="w-full text-left px-4 py-2 hover:bg-[#EFECE6] text-sm" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 inline mr-2" strokeWidth={1.5} />Sign out
                </button>
              </div>
            </div>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="nav-login">Sign in</Link>
              <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">Join</Link>
            </>
          )}
        </nav>

        <button className="md:hidden btn-ghost" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
          {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E3DD] bg-white p-4 space-y-3" data-testid="mobile-menu">
          <form onSubmit={submit} className="flex">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="input-field" />
          </form>
          <Link to="/discover" className="block py-2" onClick={() => setOpen(false)}>Discover</Link>
          <Link to="/stores" className="block py-2" onClick={() => setOpen(false)}>Stores</Link>
          <Link to="/cart" className="block py-2" onClick={() => setOpen(false)}>Cart ({count})</Link>
          {user ? (
            <>
              <Link to="/account" className="block py-2" onClick={() => setOpen(false)}>Account</Link>
              <button onClick={() => { logout(); setOpen(false); }} className="block py-2 w-full text-left">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-2" onClick={() => setOpen(false)}>Sign in</Link>
              <Link to="/register" className="block py-2" onClick={() => setOpen(false)}>Join</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
